import { createContext, useContext, useMemo } from "react";
import { useParams, useLocation } from "react-router-dom";
import { getMessage, isLocale, DEFAULT_LOCALE, LOCALES } from "@/i18n/messages";

const LocaleContext = createContext({ locale: DEFAULT_LOCALE });

export const LocaleProvider = ({ children }) => {
  const params = useParams();
  const location = useLocation();

  const locale = useMemo(() => {
    const p = params.locale;
    return p && isLocale(p) ? p : DEFAULT_LOCALE;
  }, [params.locale]);

  // Update the <html lang> attribute whenever the locale changes.
  if (typeof document !== "undefined" && document.documentElement.lang !== locale) {
    document.documentElement.lang = locale;
  }

  const value = useMemo(
    () => ({
      locale,
      pathWithoutLocale: (() => {
        const segments = location.pathname.split("/").filter(Boolean);
        if (segments.length && isLocale(segments[0])) segments.shift();
        return "/" + segments.join("/");
      })(),
    }),
    [locale, location.pathname]
  );

  return <LocaleContext.Provider value={value}>{children}</LocaleContext.Provider>;
};

export const useLocale = () => useContext(LocaleContext);

/** Translation hook. `t("home.eyebrow")` returns the current locale's string. */
export const useT = () => {
  const { locale } = useLocale();
  const t = (path, fallback = "") => getMessage(locale, path, fallback);
  return { t, locale };
};

/** Build a path with the current locale prefix.
    localePath("/services") -> "/en/services" */
export const useLocalePath = () => {
  const { locale } = useLocale();
  return (path) => {
    if (!path) return `/${locale}`;
    if (path.startsWith("http")) return path;
    // strip a leading slash, keep hash intact
    const [pathnamePart, hashPart] = path.split("#");
    const clean = pathnamePart.startsWith("/") ? pathnamePart.slice(1) : pathnamePart;
    const suffix = clean ? `/${clean}` : "";
    return `/${locale}${suffix}${hashPart ? `#${hashPart}` : ""}`;
  };
};

export { LOCALES, DEFAULT_LOCALE };
